import { memo, forwardRef } from 'react';
import { Box } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import { useWidgetContext } from 'components/Dashboard/Widget';
import { formatChartValue } from 'components/Dashboard/shared/utils';
import WidgetError from 'components/Dashboard/components/WidgetError';

const DetailsCard = memo(() => {
  const { widget, data } = useWidgetContext();
  const labelClassName = 'details-card--label';
  const valueClassName = 'details-card--value';

  const rows = !!data?.results?.length
    ? Object?.entries(data?.results?.[0])?.map(([label, value], index) => ({
        id: index,
        label,
        value,
      }))
    : [];

  const columns: GridColDef[] =
    ['label', 'value'].map((key) => ({
      field: key,
      renderHeader: () => null, // Hide the headers.
      flex: 1,
      valueFormatter: (value: any) => {
        let formattedValue;
        if (typeof value === 'number')
          formattedValue = formatChartValue({ value, format: 'number' });
        else if (value === null) formattedValue = 'N/A';
        else if (typeof value === 'string' && value.includes('_'))
          formattedValue = String(value)?.split('_').join(' ');
        return formattedValue;
      },
      cellClassName: () => {
        if (key === 'label') {
          return labelClassName;
        }
        return valueClassName;
      },
    })) ?? [];

  // If there is no data, return WidgetError.
  if (!data?.results?.length) return <WidgetError type="noData" />;

  // It will always be the first data result.
  return (
    <Box
      id={`widget-details-card--${widget?.id}`}
      data-testid={`widget-details-card--${widget?.id}`}
      sx={{ width: '100%', height: rows?.length * 52 }}
    >
      <DataGridPro
        rows={rows || []}
        columns={columns || []}
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        slots={{
          columnHeaders: forwardRef(() => null),
        }}
        sx={(theme) => {
          const isLightTheme = theme.palette.mode === 'light';
          return {
            color: isLightTheme ? theme.blox.chart.gray : theme.blox.colors.white,
            minHeight: rows?.length * 52,
            [`.${labelClassName}`]: {
              fontWeight: 'bold',
              textTransform: 'capitalize',
            },
            [`.${valueClassName}`]: {
              textTransform: 'capitalize',
            },
            '& .MuiDataGrid-scrollbar': {
              display: 'none',
            },
          };
        }}
      />
    </Box>
  );
});

DetailsCard.displayName = 'DetailsCard';

export default DetailsCard;
